import React from 'react';

import { ActionIcon, Checkbox, Group, Table, Text, Tooltip } from '@mantine/core';
import { IconChevronDown, IconChevronUp, IconSelector } from '@tabler/icons-react';

import { usePatientListFeatureFlag } from '@/hooks/usePatientListFeatureFlag';

import { ColumnDef, ColumnId, DEFAULT_COLUMNS, Density } from '../hooks/useColumnPreferences';

import { ReportFilterMenu } from './ReportFilterMenu';
import styles from './TableHeader.module.scss';

export type SortDirection = 'ASC' | 'DESC' | null;

interface TableHeaderProps {
  selectedPreviousReportFilters?: string[];
  selectedCurrentReportFilters?: string[];
  isNonPractitionerSignedCurrentReport?: boolean;
  isPendingReviewSelectedCurrentReport?: boolean;
  isRecentSignedSelected?: boolean;
  isDownloadedSelected?: boolean;
  onPreviousReportFiltersChange?: (
    filters: string[],
    isRecentSigned?: boolean,
    isNotDownloaded?: boolean
  ) => void;
  onCurrentReportFiltersChange?: (
    filters: string[],
    isNonPractitionerSelected: boolean,
    isPendingReviewSelected: boolean
  ) => void;
  isLoading?: boolean;
  sortBy?: string;
  sortDirection?: SortDirection;
  onSort?: (field: string) => void;
  isAllSelected?: boolean;
  isIndeterminate?: boolean;
  onSelectAll?: (checked: boolean) => void;
  isSelectAllDisabled?: boolean;
  columns?: ColumnDef[];
  density?: Density;
}

/** v2 columns the list API can sort by (sortBy value). */
const SORT_FIELDS: Partial<Record<ColumnId, string>> = {
  status: 'status',
  clinic: 'name'
};

/** POC sort arrow: inline, 13px, dimmed when unsorted. */
const SortIndicator: React.FC<{ direction: SortDirection }> = ({ direction }) => {
  if (direction === 'ASC') return <IconChevronUp size={13} />;
  if (direction === 'DESC') return <IconChevronDown size={13} />;
  return <IconSelector size={13} style={{ opacity: 0.5 }} />;
};

export const SortIcon: React.FC<{
  direction: SortDirection;
  onClick: () => void;
  disabled?: boolean;
}> = ({ direction, onClick, disabled = false }) => {
  const getIcon = () => {
    if (direction === 'ASC') return <IconChevronUp size={14} />;
    if (direction === 'DESC') return <IconChevronDown size={14} />;
    return <IconSelector size={14} />;
  };

  return (
    <ActionIcon
      variant="subtle"
      size="sm"
      onClick={onClick}
      disabled={disabled}
      style={{
        opacity: disabled ? 0.3 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer'
      }}>
      {getIcon()}
    </ActionIcon>
  );
};

const TableHeader: React.FC<TableHeaderProps> = ({
  selectedPreviousReportFilters = ['GREEN', 'RED', 'YELLOW'],
  selectedCurrentReportFilters = ['GREEN', 'RED', 'YELLOW'],
  isNonPractitionerSignedCurrentReport = false,
  isPendingReviewSelectedCurrentReport = false,
  onPreviousReportFiltersChange,
  onCurrentReportFiltersChange,
  isRecentSignedSelected,
  isDownloadedSelected,
  isLoading = false,
  sortBy,
  sortDirection = null,
  onSort,
  isAllSelected,
  isIndeterminate,
  onSelectAll,
  isSelectAllDisabled = false,
  columns,
  density = 'comfortable'
}) => {
  const { isPatientListV2Enabled } = usePatientListFeatureFlag();

  const activeColumns: ColumnDef[] = columns ?? DEFAULT_COLUMNS;
  const visibleColumns = activeColumns.filter((c) => c.visible);

  const handleSort = (field: string) => {
    if (onSort && !isLoading) {
      onSort(field);
    }
  };

  const getPatientSortDirection = (): SortDirection => {
    return sortBy === 'familyName' ? (sortDirection ?? null) : null;
  };

  const getClinicSortDirection = (): SortDirection => {
    return sortBy === 'name' ? (sortDirection ?? null) : null;
  };

  const getStatusSortDirection = (): SortDirection => {
    return sortBy === 'status' ? (sortDirection ?? null) : null;
  };

  const handlePreviousReportFiltersApply = (
    filters: string[],
    _: boolean,
    __: boolean,
    isRecentSigned?: boolean,
    isNotDownloaded?: boolean
  ) => {
    onPreviousReportFiltersChange?.(filters, isRecentSigned, isNotDownloaded);
  };

  const handleCurrentReportFiltersApply = (
    filters: string[],
    isNonPractitionerSelected: boolean,
    isPendingReviewSelected: boolean
  ) => {
    onCurrentReportFiltersChange?.(filters, isNonPractitionerSelected, isPendingReviewSelected);
  };

  if (!isPatientListV2Enabled) {
    return (
      <Table.Thead className={styles.header}>
        <Table.Tr>
          <Table.Th style={{ width: 40 }}>
            <Tooltip
              label="No reports to download on this page"
              disabled={!isSelectAllDisabled}
              withArrow>
              <div style={{ display: 'inline-block' }}>
                <Checkbox
                  checked={isAllSelected}
                  indeterminate={isIndeterminate}
                  onChange={(event) => onSelectAll?.(event.currentTarget.checked)}
                  disabled={isSelectAllDisabled}
                  styles={{ input: { cursor: isSelectAllDisabled ? 'not-allowed' : 'pointer' } }}
                />
              </div>
            </Tooltip>
          </Table.Th>
          <Table.Th style={{ width: 250 }}>
            <Text className={styles.tableHeading}>
              <Group gap="xs" wrap="nowrap" justify="space-between">
                Patient
                <SortIcon
                  direction={getPatientSortDirection()}
                  onClick={() => handleSort('familyName')}
                  disabled={isLoading}
                />
              </Group>
            </Text>
          </Table.Th>
          <Table.Th style={{ width: 80 }} />
          <Table.Th style={{ width: 200 }}>
            <Text className={styles.tableHeading}>
              <Group gap="xs" wrap="nowrap" justify="space-between">
                Status
                <SortIcon
                  direction={getStatusSortDirection()}
                  onClick={() => handleSort('status')}
                  disabled={isLoading}
                />
              </Group>
            </Text>
          </Table.Th>
          <Table.Th style={{ width: 200 }}>
            <Text className={styles.tableHeading}>
              <Group gap="xs" wrap="nowrap" justify="space-between">
                Clinic
                <SortIcon
                  direction={getClinicSortDirection()}
                  onClick={() => handleSort('name')}
                  disabled={isLoading}
                />
              </Group>
            </Text>
          </Table.Th>
          <Table.Th style={{ width: 150 }}>
            <Text className={styles.tableHeading}>Last Transmission</Text>
          </Table.Th>
          <Table.Th style={{ width: 300 }}>
            <Text className={styles.tableHeading}>Previous Report</Text>
          </Table.Th>
          <Table.Th style={{ width: 420 }}>
            <Text className={styles.tableHeading}>Current Report</Text>
          </Table.Th>
        </Table.Tr>
      </Table.Thead>
    );
  }

  const directionFor = (field: string): SortDirection =>
    sortBy === field ? (sortDirection ?? null) : null;

  // POC header cell: small-caps label, inline sort arrow where the API supports sorting.
  const renderHeadLabel = (label: string, sortField?: string) =>
    sortField ? (
      <button
        type="button"
        className={`${styles.headLabel} ${styles.sortable}`}
        disabled={isLoading}
        onClick={() => handleSort(sortField)}>
        {label}
        <SortIndicator direction={directionFor(sortField)} />
      </button>
    ) : (
      <span className={styles.headLabel}>{label}</span>
    );

  return (
    <Table.Thead
      className={`${styles.headerV2} ${density === 'compact' ? styles.headCompact : ''}`}>
      <Table.Tr>
        <Table.Th style={{ width: 40 }}>
          <Tooltip
            label="No reports to download on this page"
            disabled={!isSelectAllDisabled}
            withArrow>
            <div style={{ display: 'inline-block' }}>
              <Checkbox
                checked={isAllSelected}
                indeterminate={isIndeterminate}
                onChange={(event) => onSelectAll?.(event.currentTarget.checked)}
                disabled={isSelectAllDisabled}
                styles={{ input: { cursor: isSelectAllDisabled ? 'not-allowed' : 'pointer' } }}
              />
            </div>
          </Tooltip>
        </Table.Th>
        <Table.Th style={{ minWidth: 220 }}>{renderHeadLabel('Patient', 'familyName')}</Table.Th>
        <Table.Th style={{ width: 48 }} />
        {visibleColumns.map((col) => {
          switch (col.id) {
            case 'lastCompletedReport':
              return (
                <Table.Th key={col.id} style={{ minWidth: col.width }}>
                  <Text component="div" className={styles.tableHeading}>
                    <Group gap="xs" wrap="nowrap">
                      {renderHeadLabel(col.label)}
                      {!isLoading && !isPatientListV2Enabled && (
                        <ReportFilterMenu
                          selectedFilters={selectedPreviousReportFilters}
                          showNoReportOption={false}
                          showPreviousReportOptions={true}
                          isRecentSignedSelected={isRecentSignedSelected}
                          isDownloadedSelected={isDownloadedSelected}
                          onApplyFilters={handlePreviousReportFiltersApply}
                        />
                      )}
                    </Group>
                  </Text>
                </Table.Th>
              );
            case 'openReport':
              return (
                <Table.Th key={col.id} style={{ minWidth: col.width }}>
                  <Text component="div" className={styles.tableHeading}>
                    <Group gap="xs" wrap="nowrap">
                      {renderHeadLabel(col.label)}
                      {!isLoading && !isPatientListV2Enabled && (
                        <ReportFilterMenu
                          selectedFilters={selectedCurrentReportFilters}
                          isNonPractitionerSignedCRSelected={isNonPractitionerSignedCurrentReport}
                          isPendingReviewSelectedCRSelected={isPendingReviewSelectedCurrentReport}
                          onApplyFilters={handleCurrentReportFiltersApply}
                        />
                      )}
                    </Group>
                  </Text>
                </Table.Th>
              );
            default:
              return (
                <Table.Th key={col.id} style={{ minWidth: col.width }}>
                  {renderHeadLabel(col.label, SORT_FIELDS[col.id])}
                </Table.Th>
              );
          }
        })}
      </Table.Tr>
    </Table.Thead>
  );
};

export default TableHeader;
